/**
 * React hooks for trade statistics
 */

import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  dispatchStatisticsFetchError,
  normalizeStatisticsFetchError,
  type StatisticsFetchError,
} from "../utils/statisticsFetchErrors";
import {
  fetchTradeStatistics,
  fetchSymbolStatistics,
  fetchPnLOverTime,
  fetchDistribution,
  fetchRiskMetrics,
  fetchMonthlyPerformance,
  fetchWeeklyBreakdown,
  fetchDailyActivity,
  fetchComparativeAnalysis,
  fetchTimeAnalysis,
  fetchPortfolioGrowth,
  PortfolioGrowthResponse,
  fetchWinRateTrend,
  WinRateTrendResponse,
  fetchDashboardSummary,
  DashboardSummaryResponse,
  type DashboardSummaryTimeframesInput,
  ExecutionCandlesApiError,
  ExecutionCandlesBackfillAccepted,
  fetchSessionPerformance,
  type ExecutionCandlesFetchParams,
} from "../services/tradeStatisticsApi";
import { loadExecutionCandles, abortExecutionCandlesLoad } from "../utils/executionCandlesLoad";
import {
  clearExecutionCandlesResponseCache,
  readExecutionCandlesResponseCache,
  writeExecutionCandlesResponseCache,
} from "../utils/executionCandlesResponseCache";
import {
  executionCandlesCacheMatchesParams,
  executionCandlesParamsCachePayload,
} from "../utils/executionCandlesParams";
import type { MarketDataAssetClassRequiredBody } from "../types/instruments";
import {
  marketDataUnresolvedToAssetClassPayload,
  parseMarketDataAssetClassRequiredFromApiError,
  parseMarketDataUnresolvedDetail,
  isMarketDataConnectorRequiredDetail,
  isMarketDataBrokerConnectConsentRequired,
} from "../utils/marketDataResolution";
import type { MarketDataConnectOption } from "../types/marketDataAttribution";
import type { MarketDataUnresolvedDetail } from "../types/instrumentSetup";
import type { MarketDataUnavailablePayload } from "../types/marketDataUnavailable";
import {
  MarketDataUnavailableError,
  marketDataUnavailableFromBrokerAuthFailure,
  marketDataUnavailableFromExecutionCandles,
  parseMarketDataUnavailablePayload,
} from "../utils/marketDataUnavailable";
import { isPaywallRequiredError } from "../utils/paywallErrors";
import { redirectToBillingPaywall } from "../utils/billingPaywall";
import {
  BackendTradeStatistics,
  SymbolStatisticsResponse,
  TradeStatisticsFilters,
  PnlOverTimeResponse,
  DistributionResponse,
  RiskMetricsResponse,
  MonthlyPerformanceResponse,
  WeeklyBreakdownResponse,
  DailyActivityResponse,
  ComparativeAnalysisResponse,
  TimeAnalysisResponse,
  type ExecutionCandlesBackfillProgress,
  type ExecutionCandlesResponse,
  type SessionPerformanceResponse,
} from "../types/trade";

/**
 * In-flight request dedupe only — no TTL response cache.
 * Remounts / filter changes always refetch so UI stays fresh after trade changes.
 * Concurrent identical requests (same key, same tick) still share one network call.
 */
const requestCache = new Map<string, Promise<any>>();

/** When `enabled` is false, hooks skip fetch and expose null data. */

function useStatisticsHookErrorState() {
  const [fetchError, setFetchError] = useState<StatisticsFetchError | null>(null);

  const setFromErr = useCallback((err: unknown, fallback: string, cacheKey: string) => {
    const normalized = normalizeStatisticsFetchError(err, fallback);
    setFetchError(normalized);
    dispatchStatisticsFetchError(cacheKey, normalized);
  }, []);

  const clearHookError = useCallback((cacheKey: string) => {
    setFetchError(null);
    dispatchStatisticsFetchError(cacheKey, null);
  }, []);

  const setErrorMessage = useCallback((message: string, cacheKey: string) => {
    const normalized: StatisticsFetchError = { message };
    setFetchError(normalized);
    dispatchStatisticsFetchError(cacheKey, normalized);
  }, []);

  return {
    error: fetchError?.message ?? null,
    errorAction: fetchError?.action ?? null,
    errorSlug: fetchError?.slug ?? null,
    errorStatus: fetchError?.status ?? null,
    setFromErr,
    clearHookError,
    setErrorMessage,
  };
}

/** When `enabled` is false, hooks skip fetch and expose null data. */
export type TradeStatisticsHookOptions = {
  enabled?: boolean;
};

function isTradeStatisticsHookEnabled(options?: TradeStatisticsHookOptions): boolean {
  return options?.enabled !== false;
}

let tradeDataInvalidationSeq = 0;
/** Set after destructive clears while trade pages are unmounted; consumed on next trade page mount. */
let tradeDataNeedsFreshMount = false;
const tradeDataCacheResetListeners = new Set<() => void>();

function captureTradeDataInvalidationSeq(): number {
  return tradeDataInvalidationSeq;
}

function isTradeDataCacheStale(capturedSeq: number): boolean {
  return capturedSeq !== tradeDataInvalidationSeq;
}

function registerTradeDataCacheResetListener(listener: () => void): () => void {
  tradeDataCacheResetListeners.add(listener);
  return () => {
    tradeDataCacheResetListeners.delete(listener);
  };
}

function useRegisterTradeDataCacheReset(reset: () => void) {
  useEffect(() => registerTradeDataCacheResetListener(reset), [reset]);
}

/** Always miss — response TTL cache removed; callers refetch on every load. */
function readTradeDataCache<T = any>(_cacheKey: string): T | null {
  return null;
}

function applyTradeDataFetchResult<T>(
  loadSeq: number,
  cacheKey: string,
  data: T,
  apply: (value: T) => void
): void {
  if (isTradeDataCacheStale(loadSeq)) return;
  requestCache.delete(cacheKey);
  apply(data);
}

function consumeTradeDataFreshMount(): boolean {
  if (!tradeDataNeedsFreshMount) return false;
  tradeDataNeedsFreshMount = false;
  return true;
}

/** Drop caches before trade hooks' useEffects when returning after a destructive clear. */
export function useTradeDataFreshMountInvalidation() {
  useLayoutEffect(() => {
    if (consumeTradeDataFreshMount()) {
      invalidateTradeDataCaches();
    }
  }, []);
}

/**
 * Generate a stable cache key from filters
 */
function getCacheKey(prefix: string, filters?: any): string {
  if (!filters) return prefix;
  // Create stable string representation
  const normalized = JSON.stringify(filters, Object.keys(filters || {}).sort());
  return `${prefix}:${normalized}`;
}

/**
 * Clear cache entry
 */
function clearCacheEntry(key: string) {
  requestCache.delete(key);
}

/**
 * Clear all trade-related in-flight requests.
 * Call this after resyncing trades to force fresh data.
 */
export function clearAllTradeCaches() {
  resetTradeDataCaches();
}

export type TradesUpdatedDetail = {
  userId?: string;
  action?: string;
  accountCode?: string;
  brokerName?: string;
  /** When true, skip delayed refetch retries (e.g. sync-status already confirmed analytics ready). */
  skipRefetchRetries?: boolean;
};

function getTradesUpdatedAction(event: Event): string | undefined {
  return (event as CustomEvent<TradesUpdatedDetail>)?.detail?.action;
}

/** OHLC preference saves — notify chart surfaces only; trade rows/stats unchanged. */
export function isMarketDataPreferenceTradesUpdatedAction(action?: string): boolean {
  return action === "marketDataPreference";
}

function shouldSkipTradesUpdatedDataReload(event: Event): boolean {
  return isMarketDataPreferenceTradesUpdatedAction(getTradesUpdatedAction(event));
}

export function isDestructiveTradeDataAction(action?: string): boolean {
  return (
    action === "clear" ||
    action === "clearAccountTrades" ||
    action === "removeBrokerAccount"
  );
}

function shouldScheduleTradeDataRefetchRetries(detail?: TradesUpdatedDetail): boolean {
  if (detail?.skipRefetchRetries) return false;
  if (detail?.action === "brokerSync" || detail?.action === "sync") return true;
  return false;
}

/** Drop in-flight statistics requests without clearing mounted chart state. */
export function invalidateTradeDataCaches() {
  tradeDataInvalidationSeq += 1;
  requestCache.clear();
  clearExecutionCandlesResponseCache();
}

/** Drop caches and reset mounted statistics hooks (after delete / clear). */
export function resetTradeDataCaches() {
  invalidateTradeDataCaches();
  tradeDataCacheResetListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener failures so one bad subscriber cannot block invalidation.
    }
  });
}

function dispatchTradesUpdated(detail?: TradesUpdatedDetail) {
  window.dispatchEvent(new CustomEvent("tradesUpdated", { detail }));
}

const tradeDataRefetchRetryTimeoutIds = new Set<number>();

function scheduleTradeDataRefetchRetries(detail?: TradesUpdatedDetail) {
  const retryDetail: TradesUpdatedDetail = { ...detail, action: detail?.action ?? "refetch" };
  const fire = () => dispatchTradesUpdated(retryDetail);
  tradeDataRefetchRetryTimeoutIds.forEach((id) => window.clearTimeout(id));
  tradeDataRefetchRetryTimeoutIds.clear();
  tradeDataRefetchRetryTimeoutIds.add(window.setTimeout(fire, 1500));
  tradeDataRefetchRetryTimeoutIds.add(window.setTimeout(fire, 4000));
  tradeDataRefetchRetryTimeoutIds.add(window.setTimeout(fire, 8000));
  tradeDataRefetchRetryTimeoutIds.add(window.setTimeout(fire, 15000));
}

/** Clear in-memory trade/statistics caches, then notify pages to refetch. */
export function notifyTradesUpdated(detail?: TradesUpdatedDetail) {
  const marketDataOnly = isMarketDataPreferenceTradesUpdatedAction(detail?.action);
  if (isDestructiveTradeDataAction(detail?.action)) {
    tradeDataNeedsFreshMount = true;
    resetTradeDataCaches();
  } else if (!marketDataOnly) {
    invalidateTradeDataCaches();
  }
  dispatchTradesUpdated(detail);
  if (shouldScheduleTradeDataRefetchRetries(detail)) {
    scheduleTradeDataRefetchRetries(detail);
  }
}

/**
 * Hook to fetch and manage trade statistics
 */
export function useTradeStatistics(
  filters?: TradeStatisticsFilters,
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [statistics, setStatistics] = useState<BackendTradeStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();
  const abortControllerRef = useRef<AbortController | null>(null);

  const resetStatistics = useCallback(() => {
    setStatistics(null);
  }, []);
  useRegisterTradeDataCacheReset(resetStatistics);

  const loadStatistics = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (!user?.id) {
      setStatistics(null);
      return;
    }

    // If filters is undefined, don't make any calls (component is waiting for required data)
    if (!filters) {
      setStatistics(null);
      setLoading(false);
      return;
    }

    const cacheKey = getCacheKey("statistics", filters);

    // Check cache first
    const cached = readTradeDataCache<BackendTradeStatistics>(cacheKey);
    if (cached != null) {
      setStatistics(cached);
      setLoading(false);
      return;
    }

    // Check if request is already in flight
    const inFlightRequest = requestCache.get(cacheKey);
    if (inFlightRequest) {
      try {
        setLoading(true);
        const data = await inFlightRequest;
        if (!isTradeDataCacheStale(loadSeq)) {
          setStatistics(data);
          clearHookError(cacheKey);
        }
      } catch (err) {
        setFromErr(err, "Failed to load statistics", cacheKey);
        setStatistics(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        
        // Cancel previous request if exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const data = await fetchTradeStatistics(filters);

        applyTradeDataFetchResult(loadSeq, cacheKey, data, setStatistics);
        return data;
      } catch (err) {
        requestCache.delete(cacheKey);
        if (err instanceof Error && err.name !== 'AbortError') {
          if (!isTradeDataCacheStale(loadSeq)) {
            console.error("Error loading statistics:", err);
            setFromErr(err, "Failed to load statistics", cacheKey);
            setStatistics(null);
          }
        }
        throw err;
      } finally {
        setLoading(false);
      }
    })();

    requestCache.set(cacheKey, requestPromise);

    try {
      await requestPromise;
    } catch {
      // Error handling already done in promise
    }
  }, [enabled, user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    loadStatistics();
    
    return () => {
      // Cleanup: abort request on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadStatistics, enabled]);

  // Refetch when trades are synced from broker (resync success)
  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setStatistics(null);
      }
      if (filters) {
        const cacheKey = getCacheKey("statistics", filters);
        clearCacheEntry(cacheKey);
      }
      loadStatistics();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadStatistics, filters, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    if (filters) {
      const cacheKey = getCacheKey("statistics", filters);
      clearCacheEntry(cacheKey);
    }
    loadStatistics();
  }, [loadStatistics, filters, enabled]);

  return {
    statistics,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage symbol-specific statistics
 */
export function useSymbolStatistics(
  filters?: Omit<TradeStatisticsFilters, "symbol">,
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [symbolStats, setSymbolStats] = useState<SymbolStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();

    const resetCachedTradeData = useCallback(() => {
    setSymbolStats(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadSymbolStats = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      setSymbolStats(null);
      setLoading(false);
      return;
    }
    if (!user?.id) {
      setSymbolStats(null);
      return;
    }

    const cacheKey = getCacheKey("symbol-stats", filters);
    const cached = readTradeDataCache<SymbolStatisticsResponse>(cacheKey);
    if (cached != null) {
      setSymbolStats(cached);
      setLoading(false);
      return;
    }
    const inFlight = requestCache.get(cacheKey);
    if (inFlight) {
      try {
        setLoading(true);
        const data = await inFlight;
        if (!isTradeDataCacheStale(loadSeq)) {
          setSymbolStats(data);
        }
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load symbol statistics", cacheKey);
        setSymbolStats(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        const data = await fetchSymbolStatistics(filters);
        applyTradeDataFetchResult(loadSeq, cacheKey, data, setSymbolStats);
        return data;
      } catch (err) {
        requestCache.delete(cacheKey);
        console.error("Error loading symbol statistics:", err);
        setFromErr(err, "Failed to load symbol statistics", cacheKey);
        setSymbolStats(null);
        throw err;
      } finally {
        setLoading(false);
      }
    })();
    requestCache.set(cacheKey, promise);

    try {
      await promise;
    } catch {
      // handled above
    }
  }, [enabled, user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    if (!enabled) {
      setSymbolStats(null);
      setLoading(false);
      return;
    }
    loadSymbolStats();
  }, [loadSymbolStats, enabled]);

  // Refetch when trades are synced from broker (resync success); clear cache so we get fresh data
  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      setSymbolStats(null);
      clearCacheEntry(getCacheKey("symbol-stats", filters));
      loadSymbolStats();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadSymbolStats, filters, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    clearCacheEntry(getCacheKey("symbol-stats", filters));
    loadSymbolStats();
  }, [loadSymbolStats, filters, enabled]);

  return {
    symbolStats,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage P&L over time data
 */
export function usePnLOverTime(
  filters?: TradeStatisticsFilters,
  breakdown: "hourly" | "daily" | "monthly" | "weekly" = "monthly",
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [data, setData] = useState<PnlOverTimeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!user?.id) {
      setData(null);
      return;
    }

    const cacheKey = getCacheKey("pnl-over-time", { ...filters, breakdown });
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }
    const inFlight = requestCache.get(cacheKey);
    if (inFlight) {
      try {
        setLoading(true);
        const result = await inFlight;
        setData(result);
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load P&L over time", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        const result = await fetchPnLOverTime(filters, breakdown);
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        console.error("Error loading P&L over time:", err);
        setFromErr(err, "Failed to load P&L over time", cacheKey);
        setData(null);
        throw err;
      } finally {
        setLoading(false);
      }
    })();
    requestCache.set(cacheKey, promise);

    try {
      await promise;
    } catch {
      // handled above
    }
  }, [enabled, user?.id, JSON.stringify(filters), breakdown]);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    loadData();
  }, [loadData, enabled]);

  // Refetch when trades are synced from broker (resync success); clear cache so we get fresh data
  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      clearCacheEntry(getCacheKey("pnl-over-time", { ...filters, breakdown }));
      loadData();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadData, filters, breakdown, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    clearCacheEntry(getCacheKey("pnl-over-time", { ...filters, breakdown }));
    loadData();
  }, [loadData, filters, breakdown, enabled]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage distribution data
 */
export function useDistribution(
  filters?: TradeStatisticsFilters,
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [data, setData] = useState<DistributionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!user?.id) {
      setData(null);
      return;
    }

    const cacheKey = getCacheKey("distribution", filters);
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }
    const inFlight = requestCache.get(cacheKey);
    if (inFlight) {
      try {
        setLoading(true);
        const result = await inFlight;
        setData(result);
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load distribution", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        const result = await fetchDistribution(filters);
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        console.error("Error loading distribution:", err);
        setFromErr(err, "Failed to load distribution", cacheKey);
        setData(null);
        throw err;
      } finally {
        setLoading(false);
      }
    })();
    requestCache.set(cacheKey, promise);

    try {
      await promise;
    } catch {
      // handled above
    }
  }, [enabled, user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    loadData();
  }, [loadData, enabled]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      clearCacheEntry(getCacheKey("distribution", filters));
      loadData();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadData, filters, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    clearCacheEntry(getCacheKey("distribution", filters));
    loadData();
  }, [loadData, filters, enabled]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage risk metrics
 */
export function useRiskMetrics(
  filters?: TradeStatisticsFilters,
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [data, setData] = useState<RiskMetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!user?.id) {
      setData(null);
      return;
    }

    const cacheKey = getCacheKey("risk-metrics", filters);
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }
    const inFlight = requestCache.get(cacheKey);
    if (inFlight) {
      try {
        setLoading(true);
        const result = await inFlight;
        setData(result);
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load risk metrics", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        const result = await fetchRiskMetrics(filters);
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        console.error("Error loading risk metrics:", err);
        setFromErr(err, "Failed to load risk metrics", cacheKey);
        setData(null);
        throw err;
      } finally {
        setLoading(false);
      }
    })();
    requestCache.set(cacheKey, promise);

    try {
      await promise;
    } catch {
      // handled above
    }
  }, [enabled, user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    loadData();
  }, [loadData, enabled]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      clearCacheEntry(getCacheKey("risk-metrics", filters));
      loadData();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadData, filters, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    clearCacheEntry(getCacheKey("risk-metrics", filters));
    loadData();
  }, [loadData, filters, enabled]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage monthly performance data
 */
export function useMonthlyPerformance(filters?: TradeStatisticsFilters) {
  const { user } = useUser();
  const [data, setData] = useState<MonthlyPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!user?.id) {
      setData(null);
      return;
    }

    const cacheKey = getCacheKey("monthly-performance", filters);
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }
    const inFlight = requestCache.get(cacheKey);
    if (inFlight) {
      try {
        setLoading(true);
        const result = await inFlight;
        setData(result);
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load monthly performance", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        const result = await fetchMonthlyPerformance(filters);
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        console.error("Error loading monthly performance:", err);
        setFromErr(err, "Failed to load monthly performance", cacheKey);
        setData(null);
        throw err;
      } finally {
        setLoading(false);
      }
    })();
    requestCache.set(cacheKey, promise);

    try {
      await promise;
    } catch {
      // handled above
    }
  }, [user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      clearCacheEntry(getCacheKey("monthly-performance", filters));
      loadData();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadData, filters]);

  const refetch = useCallback(() => {
    clearCacheEntry(getCacheKey("monthly-performance", filters));
    loadData();
  }, [loadData, filters]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage weekly breakdown data
 */
export function useWeeklyBreakdown(filters?: TradeStatisticsFilters) {
  const { user } = useUser();
  const [data, setData] = useState<WeeklyBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!user?.id) {
      setData(null);
      return;
    }

    const cacheKey = getCacheKey("weekly-breakdown", filters);
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }
    const inFlight = requestCache.get(cacheKey);
    if (inFlight) {
      try {
        setLoading(true);
        const result = await inFlight;
        setData(result);
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load weekly breakdown", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        const result = await fetchWeeklyBreakdown(filters);
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        console.error("Error loading weekly breakdown:", err);
        setFromErr(err, "Failed to load weekly breakdown", cacheKey);
        setData(null);
        throw err;
      } finally {
        setLoading(false);
      }
    })();
    requestCache.set(cacheKey, promise);

    try {
      await promise;
    } catch {
      // handled above
    }
  }, [user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      clearCacheEntry(getCacheKey("weekly-breakdown", filters));
      loadData();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadData, filters]);

  const refetch = useCallback(() => {
    clearCacheEntry(getCacheKey("weekly-breakdown", filters));
    loadData();
  }, [loadData, filters]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage daily activity data
 */
export function useDailyActivity(
  filters?: TradeStatisticsFilters,
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [data, setData] = useState<DailyActivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();
  const abortControllerRef = useRef<AbortController | null>(null);

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    // Skip if no user
    if (!user?.id) {
      setData(null);
      setLoading(false);
      return;
    }

    // Skip if filters are empty (no broker account code)
    if (filters && (!filters.broker_account_code || filters.broker_account_code.trim() === "")) {
      setData(null);
      setLoading(false);
      return;
    }

    const cacheKey = getCacheKey("daily-activity", filters);

    // Check cache first
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }

    // Check if request is already in flight
    const inFlightRequest = requestCache.get(cacheKey);
    if (inFlightRequest) {
      try {
        setLoading(true);
        const result = await inFlightRequest;
        if (!isTradeDataCacheStale(loadSeq)) {
          setData(result);
        }
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load daily activity", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        
        // Cancel previous request if exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const result = await fetchDailyActivity(filters);
        
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error("Error loading daily activity:", err);
          setFromErr(err, "Failed to load daily activity", cacheKey);
          setData(null);
        }
        throw err;
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    })();

    // Store request in cache
    requestCache.set(cacheKey, requestPromise);

    try {
      await requestPromise;
    } catch (err) {
      // Error already handled in promise
    }
  }, [enabled, user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    loadData();
  }, [loadData, enabled]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      if (filters) clearCacheEntry(getCacheKey("daily-activity", filters));
      loadData();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadData, filters, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    if (filters) clearCacheEntry(getCacheKey("daily-activity", filters));
    loadData();
  }, [loadData, filters, enabled]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage comparative analysis data
 */
export function useComparativeAnalysis(
  filters?: TradeStatisticsFilters,
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [data, setData] = useState<ComparativeAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!user?.id) {
      setData(null);
      return;
    }

    const cacheKey = getCacheKey("comparative-analysis", filters);
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }
    const inFlight = requestCache.get(cacheKey);
    if (inFlight) {
      try {
        setLoading(true);
        const result = await inFlight;
        setData(result);
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load comparative analysis", cacheKey);
      } finally {
        setLoading(false);
      }
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        const result = await fetchComparativeAnalysis(filters);
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        console.error("Error loading comparative analysis:", err);
        setFromErr(err, "Failed to load comparative analysis", cacheKey);
        throw err;
      } finally {
        setLoading(false);
      }
    })();
    requestCache.set(cacheKey, promise);

    try {
      await promise;
    } catch {
      // handled above
    }
  }, [enabled, user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    loadData();
  }, [loadData, enabled]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      clearCacheEntry(getCacheKey("comparative-analysis", filters));
      loadData();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadData, filters, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    clearCacheEntry(getCacheKey("comparative-analysis", filters));
    loadData();
  }, [loadData, filters, enabled]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage time analysis data
 */
export function useTimeAnalysis(
  filters?: TradeStatisticsFilters,
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [data, setData] = useState<TimeAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!user?.id) {
      setData(null);
      return;
    }
    if (!filters) {
      setData(null);
      setLoading(false);
      return;
    }

    const cacheKey = getCacheKey("time-analysis", filters);
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }
    const inFlight = requestCache.get(cacheKey);
    if (inFlight) {
      try {
        setLoading(true);
        const result = await inFlight;
        setData(result);
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load time analysis", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        const result = await fetchTimeAnalysis(filters);
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        console.error("Error loading time analysis:", err);
        setFromErr(err, "Failed to load time analysis", cacheKey);
        setData(null);
        throw err;
      } finally {
        setLoading(false);
      }
    })();
    requestCache.set(cacheKey, promise);

    try {
      await promise;
    } catch {
      // handled above
    }
  }, [enabled, user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      clearCacheEntry(getCacheKey("time-analysis", filters));
      loadData();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadData, filters, enabled]);

  const refetch = useCallback(() => {
    clearCacheEntry(getCacheKey("time-analysis", filters));
    loadData();
  }, [loadData, filters]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage portfolio growth data.
 * When options.skip is true (e.g. Dashboard provides data from summary), no request is made.
 */
export function usePortfolioGrowth(
  brokerAccountCode: string,
  filters: any,
  options?: {
    skip?: boolean;
    brokerName?: string | null;
    /** From capabilities — when false, request `growth_basis=trading_pnl_only` */
    supportsPortfolioGrowth?: boolean;
  }
) {
  const { user } = useUser();
  const [data, setData] = useState<PortfolioGrowthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();
  const abortControllerRef = useRef<AbortController | null>(null);
  const skip = options?.skip === true;

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (skip) return;
    // Skip if no account code (don't make call with empty string)
    if (!user?.id || !brokerAccountCode || brokerAccountCode.trim() === "") {
      setData(null);
      setLoading(false);
      return;
    }

    const cacheKey = getCacheKey("portfolio-growth", {
      brokerAccountCode,
      brokerName: options?.brokerName,
      supportsPortfolioGrowth: options?.supportsPortfolioGrowth,
      ...filters,
    });

    // Check cache first
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }

    // Check if request is already in flight
    const inFlightRequest = requestCache.get(cacheKey);
    if (inFlightRequest) {
      try {
        setLoading(true);
        const result = await inFlightRequest;
        if (!isTradeDataCacheStale(loadSeq)) {
          setData(result);
        }
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load portfolio growth data", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        
        // Cancel previous request if exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const growth_basis =
          options?.supportsPortfolioGrowth === false ? "trading_pnl_only" : undefined;
        const result = await fetchPortfolioGrowth(brokerAccountCode, {
          ...filters,
          ...(options?.brokerName ? { broker_name: options.brokerName } : {}),
          ...(growth_basis ? { growth_basis } : {}),
        });
        
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error("Error loading portfolio growth:", err);
          setFromErr(err, "Failed to load portfolio growth data", cacheKey);
          setData(null);
        }
        throw err;
      } finally {
        setLoading(false);
      }
    })();

    requestCache.set(cacheKey, requestPromise);

    try {
      await requestPromise;
    } catch {
      // Error handling already done in promise
    }
  }, [
    skip,
    user?.id,
    brokerAccountCode,
    options?.brokerName,
    options?.supportsPortfolioGrowth,
    JSON.stringify(filters),
  ]);

  useEffect(() => {
    if (!skip) loadData();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [loadData, skip]);

  const refetch = useCallback(() => {
    if (skip) return;
    if (brokerAccountCode) {
      const cacheKey = getCacheKey("portfolio-growth", {
        brokerAccountCode,
        brokerName: options?.brokerName,
        supportsPortfolioGrowth: options?.supportsPortfolioGrowth,
        ...filters,
      });
      clearCacheEntry(cacheKey);
    }
    loadData();
  }, [loadData, brokerAccountCode, filters, skip, options?.brokerName, options?.supportsPortfolioGrowth]);

  // Refetch when trades are synced from broker (resync success)
  useEffect(() => {
    if (skip) return;
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      refetch();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [refetch, skip]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage win rate trend data.
 * When options.skip is true (e.g. Dashboard provides data from summary), no request is made.
 */
export function useWinRateTrend(
  brokerAccountCode: string,
  filters: any,
  options?: { skip?: boolean; brokerName?: string | null }
) {
  const { user } = useUser();
  const [data, setData] = useState<WinRateTrendResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();
  const abortControllerRef = useRef<AbortController | null>(null);
  const skip = options?.skip === true;

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (skip) return;
    // Skip if no account code (don't make call with empty string)
    if (!user?.id || !brokerAccountCode || brokerAccountCode.trim() === "") {
      setData(null);
      setLoading(false);
      return;
    }

    const cacheKey = getCacheKey("win-rate-trend", {
      brokerAccountCode,
      brokerName: options?.brokerName,
      ...filters,
    });

    // Check cache first
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }

    // Check if request is already in flight
    const inFlightRequest = requestCache.get(cacheKey);
    if (inFlightRequest) {
      try {
        setLoading(true);
        const result = await inFlightRequest;
        if (!isTradeDataCacheStale(loadSeq)) {
          setData(result);
        }
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load win rate trend data", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        
        // Cancel previous request if exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const result = await fetchWinRateTrend(brokerAccountCode, {
          ...filters,
          ...(options?.brokerName ? { broker_name: options.brokerName } : {}),
        });
        
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error("Error loading win rate trend:", err);
          setFromErr(err, "Failed to load win rate trend data", cacheKey);
          setData(null);
        }
        throw err;
      } finally {
        setLoading(false);
      }
    })();

    requestCache.set(cacheKey, requestPromise);

    try {
      await requestPromise;
    } catch {
      // Error handling already done in promise
    }
  }, [skip, user?.id, brokerAccountCode, options?.brokerName, JSON.stringify(filters)]);

  useEffect(() => {
    if (!skip) loadData();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [loadData, skip]);

  const refetch = useCallback(() => {
    if (skip) return;
    if (brokerAccountCode) {
      const cacheKey = getCacheKey("win-rate-trend", {
        brokerAccountCode,
        brokerName: options?.brokerName,
        ...filters,
      });
      clearCacheEntry(cacheKey);
    }
    loadData();
  }, [loadData, brokerAccountCode, filters, skip, options?.brokerName]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch execution candles + trade overlays for Trade Review.
 * Pass params from {@link buildExecutionCandlesParams} (snake_case API shape).
 */
export type UseExecutionCandlesOptions = {
  /** When false, skip fetch (params may still be defined). */
  enabled?: boolean;
  /**
   * Overlay refresh: keep chart visible and do not block UI while fetching
   * (e.g. when switching saved simulations).
   */
  softLoading?: boolean;
  /** Non–asset-class market data 422 (e.g. quote variant) — open instrument setup wizard. */
  onMarketDataUnresolved?: (detail: MarketDataUnresolvedDetail) => void;
};

export type ExecutionCandlesMarketDataUnavailable = MarketDataUnavailablePayload;

export type ExecutionCandlesConnectorRequired = {
  symbol: string;
  asset_class?: string;
  message?: string;
  action?: string;
  connect_options: MarketDataConnectOption[];
  market_data_sources?: MarketDataAssetClassRequiredBody["market_data_sources"];
  trade_analytics_available?: boolean;
};

export type ExecutionCandlesBrokerConsentRepair = {
  broker: string;
  message?: string;
};

function hasExecutionCandlesWindowParams(params: ExecutionCandlesFetchParams): boolean {
  if (params.demo_job_id?.trim() && (params.trade_ids?.length ?? 0) > 0) return true;
  return Boolean(
    params.date ||
      params.timeframe ||
      (params.start_date && params.end_date) ||
      (params.local_start_date && params.local_end_date)
  );
}

function resolveExecutionCandlesAssetClassRequired(
  err: ExecutionCandlesApiError
): MarketDataAssetClassRequiredBody | null {
  const fromDetail = parseMarketDataAssetClassRequiredFromApiError(err.body);
  if (fromDetail) return fromDetail;
  const unresolved = parseMarketDataUnresolvedDetail(err.body);
  if (unresolved) return marketDataUnresolvedToAssetClassPayload(unresolved);
  return null;
}

function notifyExecutionCandlesMarketDataUnresolved(
  err: ExecutionCandlesApiError,
  onMarketDataUnresolved?: (detail: MarketDataUnresolvedDetail) => void
): boolean {
  const unresolved = parseMarketDataUnresolvedDetail(err.body);
  if (!unresolved) return false;
  onMarketDataUnresolved?.(unresolved);
  return true;
}

function handleExecutionCandles422(
  err: ExecutionCandlesApiError,
  params: ExecutionCandlesFetchParams,
  onMarketDataUnresolved?: (detail: MarketDataUnresolvedDetail) => void
): "connector" | "broker_consent" | "asset_class" | "unresolved" | "unavailable" | null {
  const unavailable = parseMarketDataUnavailablePayload(err.body);
  if (unavailable) {
    return "unavailable";
  }

  if (isMarketDataConnectorRequiredDetail(err.body)) {
    return "connector";
  }

  const brokerConsent = isMarketDataBrokerConnectConsentRequired(err.body);
  if (brokerConsent) {
    return "broker_consent";
  }

  const required = resolveExecutionCandlesAssetClassRequired(err);
  if (required) {
    return "asset_class";
  }

  if (notifyExecutionCandlesMarketDataUnresolved(err, onMarketDataUnresolved)) {
    return "unresolved";
  }

  void params;
  return null;
}

function buildConnectorRequiredFrom422(
  err: ExecutionCandlesApiError,
  params: ExecutionCandlesFetchParams
): ExecutionCandlesConnectorRequired {
  const unresolved = parseMarketDataUnresolvedDetail(err.body);
  return {
    symbol: unresolved?.symbol ?? params.symbol ?? "",
    asset_class: unresolved?.asset_class ?? unresolved?.suggested_asset_class ?? params.asset_class,
    message: unresolved?.message,
    action: unresolved?.action,
    connect_options: unresolved?.connect_options ?? [],
    market_data_sources: unresolved?.market_data_sources,
    trade_analytics_available: unresolved?.trade_analytics_available,
  };
}

function buildMarketDataUnavailableFrom422(
  err: ExecutionCandlesApiError
): MarketDataUnavailablePayload | null {
  return parseMarketDataUnavailablePayload(err.body);
}

function reconcileExecutionCandlesResult(
  result: ExecutionCandlesResponse | null
): {
  data: ExecutionCandlesResponse | null;
  unavailable: MarketDataUnavailablePayload | null;
} {
  const brokerAuthUnavailable = marketDataUnavailableFromBrokerAuthFailure(result);
  if (brokerAuthUnavailable) {
    return { data: null, unavailable: brokerAuthUnavailable };
  }

  const unavailable = marketDataUnavailableFromExecutionCandles(result);
  if (unavailable) {
    return { data: null, unavailable };
  }
  return { data: result, unavailable: null };
}

function buildBrokerConsentRepairFrom422(
  err: ExecutionCandlesApiError
): ExecutionCandlesBrokerConsentRepair | null {
  return isMarketDataBrokerConnectConsentRequired(err.body);
}

export function useExecutionCandles(
  params?: ExecutionCandlesFetchParams | null,
  options?: UseExecutionCandlesOptions
) {
  const { user } = useUser();
  const enabled = options?.enabled !== false;
  const softLoading = options?.softLoading === true;
  const onMarketDataUnresolved = options?.onMarketDataUnresolved;
  const onMarketDataUnresolvedRef = useRef(onMarketDataUnresolved);
  onMarketDataUnresolvedRef.current = onMarketDataUnresolved;
  const [data, setData] = useState<ExecutionCandlesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillProgress, setBackfillProgress] =
    useState<ExecutionCandlesBackfillProgress | null>(null);
  const [overlayRefreshing, setOverlayRefreshing] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();
  const [assetClassRequired, setAssetClassRequired] =
    useState<MarketDataAssetClassRequiredBody | null>(null);
  const [connectorRequired, setConnectorRequired] =
    useState<ExecutionCandlesConnectorRequired | null>(null);
  const [brokerConsentRepair, setBrokerConsentRepair] =
    useState<ExecutionCandlesBrokerConsentRepair | null>(null);
  const [marketDataUnavailable, setMarketDataUnavailable] =
    useState<MarketDataUnavailablePayload | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestGenerationRef = useRef(0);
  const hasDataRef = useRef(false);
  const paramsCacheKeyRef = useRef<string | null>(null);
  const prevParamsForAbortRef = useRef<ExecutionCandlesFetchParams | null>(null);
  /** Always read latest params — do not put `params` object in loadData deps (parent re-renders thrash aborts). */
  const paramsRef = useRef(params);
  paramsRef.current = params;
  hasDataRef.current = data != null;

  const paramsCacheKey = params
    ? getCacheKey("execution-candles", executionCandlesParamsCachePayload(params))
    : "";

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    const params = paramsRef.current;
    if (
      !enabled ||
      !user?.id ||
      !params?.symbol ||
      (!params.demo_job_id?.trim() && !params?.broker_account_code) ||
      !hasExecutionCandlesWindowParams(params)
    ) {
      if (!enabled) return;
      setData(null);
      setLoading(false);
      setBackfillLoading(false);
      setBackfillProgress(null);
      setOverlayRefreshing(false);
      setAssetClassRequired(null);
      setConnectorRequired(null);
      setBrokerConsentRepair(null);
      setMarketDataUnavailable(null);
      return;
    }

    const cacheKey = getCacheKey("execution-candles", executionCandlesParamsCachePayload(params));

    const cached = readExecutionCandlesResponseCache(cacheKey, params);
    if (cached != null) {
      const reconciled = reconcileExecutionCandlesResult(cached);
      setData(reconciled.data);
      setMarketDataUnavailable(reconciled.unavailable);
      setLoading(false);
      setBackfillLoading(false);
      setBackfillProgress(null);
      setOverlayRefreshing(false);
      clearHookError(cacheKey);
      setAssetClassRequired(null);
      return;
    }

    const inFlightRequest = requestCache.get(cacheKey);
    if (inFlightRequest) {
      try {
        if (softLoading && hasDataRef.current) {
          setOverlayRefreshing(true);
        } else {
          setLoading(true);
        }
        const result = await inFlightRequest;
        if (!isTradeDataCacheStale(loadSeq) && result != null) {
          const reconciled = reconcileExecutionCandlesResult(result);
          setData(reconciled.data);
          setMarketDataUnavailable(reconciled.unavailable);
        }
        clearHookError(cacheKey);
        setAssetClassRequired(null);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        if (err instanceof MarketDataUnavailableError) {
          setMarketDataUnavailable(err.payload);
          setConnectorRequired(null);
          setBrokerConsentRepair(null);
          clearHookError(cacheKey);
          setData(null);
          setAssetClassRequired(null);
          return;
        }
        if (err instanceof ExecutionCandlesApiError && err.status === 422) {
          const kind = handleExecutionCandles422(err, paramsRef.current!, (detail) =>
            onMarketDataUnresolvedRef.current?.(detail)
          );
          if (kind === "unavailable") {
            setMarketDataUnavailable(buildMarketDataUnavailableFrom422(err));
            setConnectorRequired(null);
            setBrokerConsentRepair(null);
            clearHookError(cacheKey);
            setData(null);
            setAssetClassRequired(null);
            return;
          }
          if (kind === "connector") {
            setConnectorRequired(buildConnectorRequiredFrom422(err, paramsRef.current!));
            setBrokerConsentRepair(null);
            clearHookError(cacheKey);
            setData(null);
            setAssetClassRequired(null);
            return;
          }
          if (kind === "broker_consent") {
            setBrokerConsentRepair(buildBrokerConsentRepairFrom422(err));
            setConnectorRequired(null);
            clearHookError(cacheKey);
            setData(null);
            setAssetClassRequired(null);
            return;
          }
          if (kind === "asset_class") {
            const required = resolveExecutionCandlesAssetClassRequired(err);
            if (required) {
              setAssetClassRequired(required);
              setConnectorRequired(null);
              setBrokerConsentRepair(null);
              clearHookError(cacheKey);
              setData(null);
              return;
            }
          }
          if (kind === "unresolved") {
            setFromErr(err, "Market data setup required.", cacheKey);
            setData(null);
            setAssetClassRequired(null);
            setConnectorRequired(null);
            setBrokerConsentRepair(null);
            return;
          }
        }
        setFromErr(err, "Failed to load execution candles", cacheKey);
        setData(null);
        setAssetClassRequired(null);
        setConnectorRequired(null);
        setBrokerConsentRepair(null);
        setMarketDataUnavailable(null);
      } finally {
        setLoading(false);
        setBackfillLoading(false);
        setOverlayRefreshing(false);
      }
      return;
    }

    const requestGeneration = ++requestGenerationRef.current;

    const requestPromise = (async () => {
      try {
        if (softLoading && hasDataRef.current) {
          setOverlayRefreshing(true);
        } else {
          setLoading(true);
        }
        setBackfillLoading(false);
        setBackfillProgress(null);
        clearHookError(cacheKey);
        setAssetClassRequired(null);
        setMarketDataUnavailable(null);

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const result = await loadExecutionCandles(
          {
            ...params,
            fill_missing: params.fill_missing ?? true,
            signal: abortControllerRef.current.signal,
          },
          {
            onBackfillAccepted: (partial) => {
              if (requestGeneration !== requestGenerationRef.current) return;
              setBackfillLoading(true);
              setBackfillProgress(partial.progress ?? null);
              if (partial.candles?.length) {
                setData(partial);
              }
            },
            onBackfillProgress: (progress) => {
              if (requestGeneration !== requestGenerationRef.current) return;
              setBackfillProgress(progress);
            },
          }
        );

        if (requestGeneration !== requestGenerationRef.current) return result;

        const reconciled = reconcileExecutionCandlesResult(result);
        if (reconciled.unavailable) {
          setMarketDataUnavailable(reconciled.unavailable);
          setData(null);
          setAssetClassRequired(null);
          setConnectorRequired(null);
          setBrokerConsentRepair(null);
          clearHookError(cacheKey);
          setBackfillLoading(false);
          setBackfillProgress(null);
          return result;
        }

        writeExecutionCandlesResponseCache(cacheKey, result);
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        setAssetClassRequired(null);
        setMarketDataUnavailable(null);
        setBackfillLoading(false);
        setBackfillProgress(null);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        if (err instanceof Error && err.name === "AbortError") {
          return null;
        }
        if (requestGeneration !== requestGenerationRef.current) {
          throw err;
        }
        if (err instanceof MarketDataUnavailableError) {
          setMarketDataUnavailable(err.payload);
          setConnectorRequired(null);
          setBrokerConsentRepair(null);
          clearHookError(cacheKey);
          setData(null);
          setAssetClassRequired(null);
          return null;
        }
        if (err instanceof ExecutionCandlesApiError && err.status === 422) {
          const currentParams = paramsRef.current!;
          const kind = handleExecutionCandles422(err, currentParams, (detail) =>
            onMarketDataUnresolvedRef.current?.(detail)
          );
          if (kind === "unavailable") {
            setMarketDataUnavailable(buildMarketDataUnavailableFrom422(err));
            setConnectorRequired(null);
            setBrokerConsentRepair(null);
            clearHookError(cacheKey);
            setData(null);
            setAssetClassRequired(null);
            return null;
          }
          if (kind === "connector") {
            setConnectorRequired(buildConnectorRequiredFrom422(err, currentParams));
            setBrokerConsentRepair(null);
            clearHookError(cacheKey);
            setData(null);
            setAssetClassRequired(null);
            setMarketDataUnavailable(null);
            return null;
          }
          if (kind === "broker_consent") {
            setBrokerConsentRepair(buildBrokerConsentRepairFrom422(err));
            setConnectorRequired(null);
            clearHookError(cacheKey);
            setData(null);
            setAssetClassRequired(null);
            setMarketDataUnavailable(null);
            return null;
          }
          if (kind === "asset_class") {
            const required = resolveExecutionCandlesAssetClassRequired(err);
            if (required) {
              setAssetClassRequired(required);
              setConnectorRequired(null);
              setBrokerConsentRepair(null);
              clearHookError(cacheKey);
              setData(null);
              setMarketDataUnavailable(null);
              return null;
            }
          }
          if (kind === "unresolved") {
            setErrorMessage("Market data setup required for this symbol.", cacheKey);
            setData(null);
            setAssetClassRequired(null);
            setConnectorRequired(null);
            setBrokerConsentRepair(null);
            setMarketDataUnavailable(null);
            return null;
          }
        }
        if (err instanceof ExecutionCandlesBackfillAccepted) {
          return null;
        }
        console.error("Error loading execution candles:", err);
        setFromErr(err, "Failed to load execution candles", cacheKey);
        setData(null);
        setAssetClassRequired(null);
        setConnectorRequired(null);
        setBrokerConsentRepair(null);
        setMarketDataUnavailable(null);
        throw err;
      } finally {
        // Clear loading for this generation. Superseded requests skip this so the
        // new request owns loading (and cleanup forces false on unmount).
        if (requestGeneration === requestGenerationRef.current) {
          setLoading(false);
          setBackfillLoading(false);
          setOverlayRefreshing(false);
        }
      }
    })();

    requestCache.set(cacheKey, requestPromise);

    try {
      await requestPromise;
    } catch {
      // handled above
    }
  }, [
    enabled,
    softLoading,
    user?.id,
    paramsCacheKey,
    clearHookError,
    setFromErr,
    setErrorMessage,
  ]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setBackfillLoading(false);
      setOverlayRefreshing(false);
      return;
    }

    // Abort previous only when the logical request key changes — never because
    // parent recreated a params object with the same payload (accordion thrash).
    if (paramsCacheKey && paramsCacheKeyRef.current !== paramsCacheKey) {
      if (prevParamsForAbortRef.current) {
        abortExecutionCandlesLoad(prevParamsForAbortRef.current);
      }
      requestGenerationRef.current += 1;
      abortControllerRef.current?.abort();
      paramsCacheKeyRef.current = paramsCacheKey;
    } else if (paramsCacheKey && !paramsCacheKeyRef.current) {
      paramsCacheKeyRef.current = paramsCacheKey;
    }

    if (!paramsCacheKey) return;

    void loadData();
    prevParamsForAbortRef.current = paramsRef.current ?? null;
  }, [enabled, loadData, paramsCacheKey]);

  // Unmount only — do not cancel in-flight work on every parent re-render.
  useEffect(() => {
    return () => {
      requestGenerationRef.current += 1;
      abortControllerRef.current?.abort();
    };
  }, []);

  const refetch = useCallback(() => {
    const current = paramsRef.current;
    if (current) {
      const cacheKey = getCacheKey(
        "execution-candles",
        executionCandlesParamsCachePayload(current)
      );
      clearCacheEntry(cacheKey);
      clearExecutionCandlesResponseCache(cacheKey);
      abortExecutionCandlesLoad(current);
    }
    void loadData();
  }, [loadData]);

  const dismissAssetClassRequired = useCallback(() => {
    setAssetClassRequired(null);
  }, []);

  const dismissConnectorRequired = useCallback(() => {
    setConnectorRequired(null);
  }, []);

  const dismissBrokerConsentRepair = useCallback(() => {
    setBrokerConsentRepair(null);
  }, []);

  const dismissMarketDataUnavailable = useCallback(() => {
    setMarketDataUnavailable(null);
  }, []);

  return {
    data,
    loading,
    backfillLoading,
    backfillProgress,
    overlayRefreshing,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    assetClassRequired,
    dismissAssetClassRequired,
    connectorRequired,
    dismissConnectorRequired,
    brokerConsentRepair,
    dismissBrokerConsentRepair,
    marketDataUnavailable,
    dismissMarketDataUnavailable,
    refetch,
  };
}

/**
 * Hook to fetch dashboard summary (lightweight single-call endpoint)
 * This replaces multiple separate API calls with a single optimized endpoint
 */
export function useDashboardSummary(
  brokerAccountCode: string,
  timeframes: DashboardSummaryTimeframesInput,
  brokerName?: string | null,
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [data, setData] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();
  const abortControllerRef = useRef<AbortController | null>(null);

  const resetDashboardSummary = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetDashboardSummary);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      // Keep last good data — toggling enabled during layout hydrate must not wipe the dashboard.
      setLoading(false);
      return;
    }
    // Skip if no account code
    if (!user?.id || !brokerAccountCode || brokerAccountCode.trim() === "") {
      setData(null);
      setLoading(false);
      return;
    }

    const cacheKey = getCacheKey("dashboard-summary", {
      brokerAccountCode,
      brokerName,
      ...timeframes,
    });

    // Check cache first
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }

    // Check if request is already in flight
    const inFlightRequest = requestCache.get(cacheKey);
    if (inFlightRequest) {
      try {
        setLoading(true);
        const result = await inFlightRequest;
        if (!isTradeDataCacheStale(loadSeq)) {
          setData(result);
          clearHookError(cacheKey);
        }
      } catch (err) {
        if (isPaywallRequiredError(err)) {
          redirectToBillingPaywall();
          clearHookError(cacheKey);
          setData(null);
        } else {
          setFromErr(err, "Failed to load dashboard summary", cacheKey);
          setData(null);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        
        // Cancel previous request if exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const result = await fetchDashboardSummary(brokerAccountCode, timeframes, brokerName);

        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        if (err instanceof Error && err.name !== 'AbortError') {
          if (isPaywallRequiredError(err)) {
            redirectToBillingPaywall();
            clearHookError(cacheKey);
            setData(null);
            return;
          }
          if (!isTradeDataCacheStale(loadSeq)) {
            console.error("Error loading dashboard summary:", err);
            setFromErr(err, "Failed to load dashboard summary", cacheKey);
            setData(null);
          }
        }
        throw err;
      } finally {
        setLoading(false);
      }
    })();

    requestCache.set(cacheKey, requestPromise);

    try {
      await requestPromise;
    } catch {
      // Error handling already done in promise
    }
  }, [enabled, user?.id, brokerAccountCode, brokerName, JSON.stringify(timeframes)]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    loadData();
    
    return () => {
      // Cleanup: abort request on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    if (brokerAccountCode) {
      const cacheKey = getCacheKey("dashboard-summary", {
        brokerAccountCode,
        brokerName,
        ...timeframes,
      });
      clearCacheEntry(cacheKey);
    }
    loadData();
  }, [loadData, brokerAccountCode, brokerName, timeframes, enabled]);

  // Refetch when trades are synced from broker (resync). Skip on account switch (detail.accountCode) – parent will set new account and this hook will refetch via loadData dependency.
  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      const detail = (event as CustomEvent<TradesUpdatedDetail>)?.detail;
      if (detail?.accountCode && detail?.action === "accountSwitch") return;
      if (isDestructiveTradeDataAction(detail?.action)) {
        setData(null);
      }
      refetch();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [refetch, enabled]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}

/**
 * Hook to fetch and manage session performance statistics
 */
export function useSessionPerformance(
  filters?: TradeStatisticsFilters,
  options?: TradeStatisticsHookOptions
) {
  const { user } = useUser();
  const enabled = isTradeStatisticsHookEnabled(options);
  const [data, setData] = useState<SessionPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { error, errorAction, errorSlug, errorStatus, setFromErr, clearHookError, setErrorMessage } = useStatisticsHookErrorState();

    const resetCachedTradeData = useCallback(() => {
    setData(null);
  }, []);
  useRegisterTradeDataCacheReset(resetCachedTradeData);

  const loadData = useCallback(async () => {
    const loadSeq = captureTradeDataInvalidationSeq();
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!user?.id) {
      setData(null);
      return;
    }

    const cacheKey = getCacheKey("session-performance", filters);
    const cached = readTradeDataCache(cacheKey);
    if (cached != null) {
      setData(cached);
      setLoading(false);
      return;
    }
    const inFlight = requestCache.get(cacheKey);
    if (inFlight) {
      try {
        setLoading(true);
        const result = await inFlight;
        setData(result);
        clearHookError(cacheKey);
      } catch (err) {
        setFromErr(err, "Failed to load session performance", cacheKey);
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        clearHookError(cacheKey);
        const result = await fetchSessionPerformance(filters);
        applyTradeDataFetchResult(loadSeq, cacheKey, result, setData);
        return result;
      } catch (err) {
        requestCache.delete(cacheKey);
        console.error("Error loading session performance:", err);
        setFromErr(err, "Failed to load session performance", cacheKey);
        setData(null);
        throw err;
      } finally {
        setLoading(false);
      }
    })();
    requestCache.set(cacheKey, promise);

    try {
      await promise;
    } catch {
      // handled above
    }
  }, [enabled, user?.id, JSON.stringify(filters)]);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    loadData();
  }, [loadData, enabled]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      if (shouldSkipTradesUpdatedDataReload(event)) return;
      if (!enabled) return;
      if (isDestructiveTradeDataAction(getTradesUpdatedAction(event))) {
        setData(null);
      }
      clearCacheEntry(getCacheKey("session-performance", filters));
      loadData();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [loadData, filters, enabled]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    clearCacheEntry(getCacheKey("session-performance", filters));
    loadData();
  }, [loadData, filters, enabled]);

  return {
    data,
    loading,
    error,
    errorAction,
    errorSlug,
    errorStatus,
    refetch,
  };
}